'use client';
import { createContext, ReactNode, Dispatch } from 'react';

import { BrokerAction } from '@/components/context/ContextBroker/ContextBroker';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { WishlistContextValue } from '@/components/context/WishlistContext/WishlistContext';

import { CACHE_TIME } from '@/const/cache';

import analytics from '@/util/analytics';
import { login, logout } from '@/util/auth';
import { noop } from '@/util/misc';

import { Cart } from '@/types/Cart';
import { Closet } from '@/types/Closet';
import { PaymentCustomer, PaymentMethod } from '@/types/Payment';
import { User } from '@/types/User';
import { closeDatadogRUM, initializeDatadogRUM } from '@/util/datadog';

export class AcceptPriveeError extends Error {
  paymentFailed: boolean;

  constructor(paymentFailed: boolean) {
    super('Failed to accept Réservé invitation');
    this.name = 'AcceptPriveeError';
    this.paymentFailed = paymentFailed;
  }
}

export type UserContextValue = User;

export type UserBrokerActions = {
  login: (username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  update: (data: Partial<User>) => Promise<User>;
  acceptPriveeInvitation: (selectVipInsurance?: boolean) => Promise<User>;
  joinPriveeWaitlist: (selectVipInsurance?: boolean) => Promise<User>;
  updatePassword: (password: string, newPassword: string) => Promise<boolean>;
  refresh: (userData?: User) => Promise<User>;
};

export type UserBrokeredValue = {
  value?: UserContextValue;
  actions: UserBrokerActions;
};

export const UserContext = createContext<UserBrokeredValue>({
  value: undefined,
  actions: {
    login: noop,
    logout: noop,
    update: noop,
    acceptPriveeInvitation: noop,
    joinPriveeWaitlist: noop,
    updatePassword: noop,
    refresh: noop
  }
});

type UserProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function UserProvider(props: UserProviderProps) {
  const { children, dispatch } = props;
  const brokerValue = useContextBroker();

  const TEMP_WISHLIST_KEY = 'TEMP_WISHLIST';

  const decoratedValue: UserBrokeredValue = {
    value: brokerValue.user,
    actions: {
      login: async (username: string, password: string) => {
        const response = await login(username, password);
        if (response.data === undefined) {
          throw Error(
            "Login response worked, but did not throw an error, so I'm throwing an error here because it's important"
          );
        }

        const user = response.data;

        // After the user logs in, hydrate the user.
        // This is repeated code, but an unfortunate loop we need to jump through right now
        await refreshUserDetails();
        dispatch({ type: 'USER_LOGIN', payload: user });

        // Merge temporary wishlist into user's real wishlist
        const tempWishlistStr = localStorage.getItem(TEMP_WISHLIST_KEY);

        if (tempWishlistStr) {
          try {
            const tempWishlist = JSON.parse(tempWishlistStr);
            // Add each item from temp wishlist to real wishlist
            for (const item of tempWishlist) {
              try {
                const response = await fetch('/api/wishlist', {
                  method: 'POST',
                  body: JSON.stringify({
                    sku: item.sku ?? item.item_group_id,
                    itemGroupId: item.item_group_id,
                    userId: user.id
                  })
                });

                // If we get a 401, refresh user state and retry once
                if (response.status === 401) {
                  await refreshUserDetails();

                  // Retry the request
                  const retryResponse = await fetch('/api/wishlist', {
                    method: 'POST',
                    body: JSON.stringify({
                      sku: item.sku ?? item.item_group_id,
                      itemGroupId: item.item_group_id,
                      userId: user.id
                    })
                  });

                  if (!retryResponse.ok) {
                    throw new Error(
                      `Wishlist request failed after retry: ${retryResponse.status}`
                    );
                  }
                } else if (!response.ok) {
                  throw new Error(
                    `Wishlist request failed: ${response.status}`
                  );
                }
              } catch (error) {
                console.error('Error adding item to wishlist:', error);
                // Continue with next item even if this one failed
              }
            }
            // Clear temp wishlist from localStorage and state
            localStorage.removeItem(TEMP_WISHLIST_KEY);
            dispatch({ type: 'WISHLIST_CLEAR_TEMP' });
          } catch (error) {
            console.error('Error merging temporary wishlist:', error);
          }
        }

        await refreshUserDetails();

        // Identify the user via Segment
        const name = `${user.firstName} ${user.lastName}`;
        analytics.identify(user.id, {
          // When identifying users, you can send an arbitrary
          // number of "traits" as key-value pairs. It is important
          // to keep `reserved traits` in Segment as their intended
          // value. See the following URL for more information
          // https://segment.com/docs/connections/spec/identify/#traits
          name,
          email: user.email,
          subscriptionOrderId: user.subscriptionOrderId,
          stripeCustomerId: user.paymentCustomerId
        });

        // Login doesn't reload the page, so BrazeInit won't identify the user until the next full load
        // Dynamic import: the Braze SDK reads `navigator` when loaded, which breaks server rendering
        import('@/components/common/BrazeInit/braze-exports')
          .then(({ default: braze }) => {
            if (braze.isInitialized()) braze.changeUser(user.id);
          })
          .catch(() => {
            // Silent error - don't block login
          });

        initializeDatadogRUM();

        return user;
      },

      logout: async () => {
        // Server first: if it does not confirm, this throws before anything is
        // cleared, so the UI never shows a logged-out member whose session is
        // still alive. The caller keeps the member on the page with a retry.
        await logout();

        // Clear out all values in context broker
        dispatch({ type: 'WISHLIST_CLEAR' });
        dispatch({ type: 'CLOSET_CLEAR' });
        dispatch({ type: 'PAYMENT_CLEAR' });
        dispatch({ type: 'CART_CLEAR' });
        dispatch({ type: 'USER_LOGOUT' });

        if (window?.analytics) {
          window.analytics.reset();
        }

        closeDatadogRUM();
      },

      update: async (data: Partial<User>) => {
        const updateUserResponse = await fetch(`/api/user`, {
          method: 'PUT',
          body: JSON.stringify({
            id: brokerValue?.user?.id,
            ...data
          })
        });

        const updatedUser = (await updateUserResponse.json()).body as User;

        dispatch({ type: 'USER_UPDATE', userData: updatedUser });

        return updatedUser;
      },

      acceptPriveeInvitation: async (selectVipInsurance = false) => {
        const response = await fetch('/api/user/privee/accept-invitation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selectVipInsurance })
        });

        if (!response.ok) {
          let paymentFailed = false;
          try {
            const body = await response.json();
            paymentFailed = body.paymentFailed === true;
          } catch {
            // If we can't parse the response body, treat as generic error
          }
          throw new AcceptPriveeError(paymentFailed);
        }

        const updatedUser = (await response.json()).body as User;
        dispatch({ type: 'USER_UPDATE', userData: updatedUser });
        return updatedUser;
      },

      joinPriveeWaitlist: async (selectVipInsurance = false) => {
        const response = await fetch('/api/user/privee/join-waitlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selectVipInsurance })
        });

        if (!response.ok) {
          throw new Error('Failed to join Réservé waitlist');
        }

        const updatedUser = (await response.json()).body as User;
        dispatch({ type: 'USER_UPDATE', userData: updatedUser });
        return updatedUser;
      },

      updatePassword: async (password: string, newPassword: string) => {
        const updateUserResponse = await fetch(`/api/user/password`, {
          method: 'PUT',
          body: JSON.stringify({
            currentPassword: password,
            newPassword
          })
        });
        const updateSuccess = await updateUserResponse.json();
        if (updateSuccess.success && updateSuccess.signedOut) {
          // BFF2 ends every session for the customer on a password change,
          // including this one (ADR 0008), so sign in again.
          window.location.assign('/login?updated');
        }
        return updateSuccess.success || false;
      },

      refresh: async (userData?: User) => {
        let user: User;

        if (userData) {
          // Use provided user data (e.g., from signup auto-login)
          // Skip API call and just update context directly
          user = userData;
        } else {
          // Get fresh User object from API (requires authentication)
          const userResponse = await fetch('/api/user', {
            method: 'GET',
            next: { revalidate: CACHE_TIME.NO_CACHE },
            headers: {
              'content-type': 'application/json'
            }
          });

          user = (await userResponse.json()).body as User;
        }

        // Refresh user details (wishlist, closet, cart, payment)
        await refreshUserDetails();

        dispatch({
          type: 'USER_REFRESH',
          newContextValue: user
        });

        return user;
      }
    }
  };

  /**
   * Utilize this helper to refresh ancillary data surrounding the user
   * (does not include refreshing the user itself)
   */
  const refreshUserDetails = async () => {
    const allResponses = await Promise.allSettled([
      fetch('/api/wishlist', {
        method: 'GET',
        next: {
          revalidate: CACHE_TIME.NO_CACHE
        },
        headers: {
          'content-type': 'application/json'
        }
      }),
      fetch('/api/closet', {
        method: 'GET',
        next: { revalidate: CACHE_TIME.NO_CACHE },
        headers: {
          'content-type': 'application/json'
        }
      }),
      fetch('/api/cart', {
        method: 'GET',
        next: { revalidate: CACHE_TIME.NO_CACHE },
        headers: {
          'content-type': 'application/json'
        }
      }),
      fetch('/api/payment/customer', {
        method: 'GET',
        next: { revalidate: CACHE_TIME.NO_CACHE },
        headers: {
          'content-type': 'application/json'
        }
      }),
      fetch('/api/payment/method', {
        method: 'GET',
        next: { revalidate: CACHE_TIME.NO_CACHE },
        headers: {
          'content-type': 'application/json'
        }
      })
    ]);

    const [
      wishlistResp,
      closetResp,
      cartResp,
      paymentCustomerResp,
      paymentMethodResp
    ] = allResponses;

    if (
      wishlistResp.status === 'fulfilled' &&
      wishlistResp.value.status === 200
    ) {
      let wishlistJson;

      try {
        wishlistJson = await wishlistResp.value.json();
      } catch (e) {
        console.error(e);
      }

      if (wishlistJson?.data) {
        const unWrappedWishlist = wishlistJson as {
          success: boolean;
          data: WishlistContextValue;
        };
        dispatch({
          type: 'WISHLIST_REFRESH',
          newContextValue: unWrappedWishlist.data
        });
      }
    } else {
      // Wishlist call failed!
      console.error(
        '[UserContext refreshUserDetails()] - Failed to load user wishlist'
      );
    }

    if (closetResp.status === 'fulfilled' && closetResp.value.status === 200) {
      const closetJson = await closetResp.value.json();
      const unwrappedCloset = closetJson as unknown as {
        success: boolean;
        data: Closet;
      };

      dispatch({
        type: 'CLOSET_REFRESH',
        newContextValue: {
          closet: unwrappedCloset.data
        }
      });
    } else {
      // Closet call failed!
      console.error(
        '[UserContext refreshUserDetails()] - Failed to load user closet'
      );
    }

    if (cartResp.status === 'fulfilled' && cartResp.value.status === 200) {
      const cartJson = await cartResp.value.json();
      const unwrappedCart = cartJson as unknown as {
        success: boolean;
        data: Cart;
      };

      dispatch({
        type: 'CART_REFRESH',
        newContextValue: unwrappedCart.data
      });
    } else {
      // cart call failed!
      console.error(
        '[UserContext refreshUserDetails()] - Failed to load user cart'
      );
    }

    // TODO: Break this down into 2 methods/dispatches so it's not clumped together
    if (
      paymentCustomerResp.status === 'fulfilled' &&
      paymentCustomerResp.value.status === 200 &&
      paymentMethodResp.status === 'fulfilled' &&
      paymentMethodResp.value.status === 200
    ) {
      const paymentCustomerJson = await paymentCustomerResp.value.json();
      const methodCustomerJson = await paymentMethodResp.value.json();

      const unwrappedPaymentCustomer = paymentCustomerJson as unknown as {
        success: boolean;
        data: PaymentCustomer;
      };

      const unwrappedPaymentMethod = methodCustomerJson as unknown as {
        data: PaymentMethod;
      };

      dispatch({
        type: 'PAYMENT_REFRESH',
        newContextValue: {
          customer: unwrappedPaymentCustomer.data,
          method: unwrappedPaymentMethod.data
        }
      });
    } else {
      // Payment call failed!
      console.error(
        '[UserContext refreshUserDetails()] - Failed to load user payment data and/or payment methods'
      );
    }
  };

  return (
    <UserContext.Provider value={decoratedValue}>
      {children}
    </UserContext.Provider>
  );
}

export default UserProvider;
